/* eslint-disable react/forbid-dom-props */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  User, Building2, FileText, Clock, AlertTriangle,
  Save, Zap, CheckCircle, Loader,
  ChevronLeft, ChevronRight, Scale, Bot, Printer, Upload, Send, Paperclip,
  Archive, RotateCcw, Trash2, Minus, X, Copy
} from 'lucide-react';

import { useFormStore, FIELD_LABELS } from '../../store/useFormStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useAutoSave } from '../../hooks/useAutoSave';
import { casesApi, formsApi, petitionsApi } from '../../services/api';
import { ConfirmModal } from '../../components/ui';
import { CustomFieldsBlock, TabIdentificacao, TabReclamante, TabReclamado, TabContrato, TabJornada, TabFatosEspeciais, TabJuridico, TabValoresPedidos, TabPromptMaster, PEDIDOS_DEF, rotuloDoPedido } from './tabs/AllTabs';
import ConfirmGenerationModal from './ConfirmGenerationModal';
import PerguntasPanel from '../../components/PerguntasPanel';
import PeticaoReprovadaPanel from '../../components/PeticaoReprovadaPanel';
import { PERMISSIONS } from '../../rbac/permissions';
import { mergeImportedFields } from '../../utils/intakeImportMerge';
import { mensagemDoErro } from '../../utils/erroDaApi';
import { triggerBlobDownload } from '../../utils/download';

// Formatos aceitos na importação: texto (lido no servidor) e imagem/scan (lido pela IA multimodal).
const IMPORT_ACCEPT = '.doc,.docx,.pdf,.png,.jpg,.jpeg,.webp,.txt,.md';
const ARRAY_DIFF_TITLES = {
  defendants: 'Reclamado (Empresa)',
  contracts: 'Contrato',
  claimant_phones: 'Telefone',
};

// Imagem sai da aplicação como está: não há como mascarar dado pessoal dentro de um bitmap,
// então o atendente confirma antes do envio ao provedor externo (docs/LGPD.md). Texto (DOCX/PDF)
// não pede confirmação porque é pseudonimizado no servidor antes de sair.
function confirmarEnvioDeImagem(file) {
  if (!/\.(png|jpe?g|webp)$/i.test(file?.name || '')) return true;
  return window.confirm(
    `"${file.name}" será enviado ao provedor de IA como imagem, sem mascaramento dos dados ` +
    'pessoais que aparecerem nela (RG, CPF, CTPS).\n\n' +
    'Para preencher a ficha sem enviar documento para fora, use o modelo de ficha em Word.\n\n' +
    'Deseja continuar?'
  );
}

const LOCKED_FICHA_STATUSES = ['aguardando_protocolo', 'protocolado'];
const PROTOCOLLED_OR_LATER_STATUSES = ['protocolado'];

const TABS = [
  { id: 'identificacao', label: 'Identificação',   icon: FileText,       component: TabIdentificacao  },
  { id: 'reclamante',    label: 'Reclamante',       icon: User,           component: TabReclamante    },
  { id: 'reclamado',     label: 'Reclamado',        icon: Building2,      component: TabReclamado     },
  { id: 'contrato',      label: 'Contrato',         icon: FileText,       component: TabContrato      },
  { id: 'jornada',       label: 'Jornada',          icon: Clock,          component: TabJornada       },
  { id: 'fatos',         label: 'Fatos Especiais',  icon: AlertTriangle,  component: TabFatosEspeciais },
  { id: 'juridico',      label: 'Jurídico',         icon: Scale,          component: TabJuridico      },
  { id: 'valores',       label: 'Valores Pedidos',  icon: Scale,          component: TabValoresPedidos },
  { id: 'prompt',        label: 'Prompt Master',    icon: Bot,            component: TabPromptMaster  },
];

function SaveStatus({ status }) {
  const map = {
    idle:    { cls: 'autosave-idle',    icon: <Save size={11} />,           text: 'Auto-save ativo' },
    saving:  { cls: 'autosave-saving',  icon: <div className="spinner" style={{ width: 11, height: 11 }} />, text: 'Salvando...' },
    saved:   { cls: 'autosave-saved',   icon: <CheckCircle size={11} />,    text: 'Salvo' },
    error:   { cls: 'autosave-error',   icon: <AlertTriangle size={11} />,  text: 'Erro ao salvar' },
  };
  const s = map[status] || map.idle;
  return (
    <div className={`status-bar`}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem' }} className={s.cls}>
        {s.icon} {s.text}
      </span>
    </div>
  );
}

function fichaSaveErrorMessage(err, fallback = 'Erro ao salvar') {
  const detail = err?.response?.data?.detail;
  if (detail?.blocked_fields) {
    const count = detail.blocked_fields.length;
    return `${detail.message || 'Ficha travada para edição.'} ${count} campo${count === 1 ? '' : 's'} bloqueado${count === 1 ? '' : 's'}.`;
  }
  return mensagemDoErro(err, fallback);
}

function apiDetailMessage(err, fallback = 'Erro inesperado') {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

function fieldLabel(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function FichaActionToolbar({
  archived,
  canAddToCorpus,
  canArchive,
  canDelete,
  completenessPct,
  corpusAdded,
  isAddingToCorpus,
  isGenerating,
  isSaving,
  isArchiving,
  isDeleting,
  isPreparingGeneration,
  onArchiveToggle,
  onAddToCorpus,
  onDelete,
  onGenerate,
  onImport,
  onPrint,
  onSave,
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '.75rem',
        flexWrap: 'wrap',
        padding: '.75rem .875rem',
        border: '1px solid var(--border-light)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-card)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-outline btn-sm ficha-action-btn" onClick={onImport}>
          <Bot size={13} /> Importar com IA
        </button>
        <button type="button" className="btn btn-outline btn-sm ficha-action-btn" onClick={onPrint}>
          <Printer size={13} /> Imprimir
        </button>
        {canArchive && (
          <button type="button" className="btn btn-outline btn-sm ficha-action-btn" onClick={onArchiveToggle} disabled={isArchiving}>
            {archived ? <RotateCcw size={13} /> : <Archive size={13} />}
            {isArchiving ? 'Atualizando...' : archived ? 'Desarquivar' : 'Arquivar'}
          </button>
        )}
        {canAddToCorpus && (
          <button
            type="button"
            className="btn btn-outline btn-sm ficha-action-btn"
            onClick={onAddToCorpus}
            disabled={isAddingToCorpus || corpusAdded}
          >
            {corpusAdded ? <CheckCircle size={13} /> : <Upload size={13} />}
            {corpusAdded
              ? 'Adicionado ao corpus'
              : isAddingToCorpus
                ? 'Adicionando...'
                : 'Adicionar ao corpus de curadoria'}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
        {canDelete && (
          <button type="button" className="btn btn-danger btn-sm ficha-action-btn" onClick={onDelete} disabled={isDeleting}>
            <Trash2 size={13} /> {isDeleting ? 'Excluindo...' : 'Excluir'}
          </button>
        )}
        <button type="button" className="btn btn-primary btn-sm ficha-action-btn" onClick={onSave} disabled={isSaving}>
          {isSaving ? <><div className="spinner" /> Salvando...</> : <><Save size={13} /> Salvar</>}
        </button>
        <button
          type="button"
          className="btn btn-gold btn-sm ficha-action-btn"
          onClick={onGenerate}
          disabled={isGenerating || isPreparingGeneration || completenessPct < 50}
          title={completenessPct < 50 ? 'Preencha pelo menos 50% da ficha' : 'Gerar petição inicial com IA'}
        >
          {isGenerating || isPreparingGeneration
            ? <><Loader size={14} style={{ animation: 'spin .65s linear infinite' }} /> Processando...</>
            : <><Zap size={14} /> Gerar Petição Inicial</>
          }
        </button>
      </div>
    </div>
  );
}

/**
 * Por que a última geração parou, enquanto o caso estiver na ficha por causa dela.
 *
 * O motivo (crédito, chave, modelo) aparecia só no card do Kanban e num toast que some: quem voltava
 * à ficha para gerar de novo não via o que tinha dado errado (relatório do Dr. Lucas de 13/10/2026,
 * item 4.6). Reprovação do portão não entra aqui — a peça existe e o painel dela diz o que fazer.
 */
export function AvisoFalhaDaGeracao({ caseId, caseStatus }) {
  const naFicha = caseStatus === 'ficha_preenchimento';
  const { data } = useQuery({
    queryKey: ['generation-status', caseId],
    queryFn: () => casesApi.getGenerationStatus(caseId),
    enabled: !!caseId && naFicha,
  });
  if (!naFicha || !data?.falha_geracao) return null;
  return (
    <div className="alert alert-error" role="alert">
      <AlertTriangle size={16} />
      <span>
        <strong>A última geração da petição não foi concluída.</strong> {data.falha_geracao}{' '}
        A ficha continua salva: resolva o que a mensagem indica e gere de novo.
      </span>
    </div>
  );
}

export default function FormularioFicha() {
  const { id: caseIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const {
    caseId, contactId, formData, completeness, saveStatus,
    isGenerating, activeTab,
    setCaseId, loadFromCase,
    setActiveTab, setIsGenerating, reset, setSaveStatus, setLockMode,
  } = useFormStore();

  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleImportClick = async () => {
    if (caseId) {
      setImportDrawerOpen(true);
      return;
    }

    const toastId = toast.loading('Criando ficha rascunho para importação...');
    try {
      const nc = await formsApi.createWithCase(contactId, formData);
      setCaseId(nc.id);
      navigate(`/ficha/${nc.id}`, { replace: true });
      toast.success('Rascunho criado.', { id: toastId });
      setImportDrawerOpen(true);
    } catch (err) {
      toast.error(fichaSaveErrorMessage(err, 'Erro ao criar rascunho antes da importação.'), { id: toastId });
    }
  };

  async function handleTabChange(index) {
    if (index === activeTab) return;
    const currentFormData = useFormStore.getState().formData;
    if (caseId) {
      try {
        setSaveStatus('saving');
        await formsApi.save(caseId, currentFormData);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
      } catch (err) {
        setSaveStatus('error');
        toast.error(fichaSaveErrorMessage(err));
      }
    } else if (contactId) {
      try {
        setSaveStatus('saving');
        const nc = await formsApi.createWithCase(contactId, currentFormData);
        setCaseId(nc.id);
        navigate(`/ficha/${nc.id}`, { replace: true });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
      } catch (err) {
        setSaveStatus('error');
        toast.error(fichaSaveErrorMessage(err, 'Erro ao criar ficha'));
      }
    }
    setActiveTab(index);
  }

  useAutoSave(30000);

  // Carregar caso existente
  const { isLoading, data: caseData, isSuccess: caseLoaded } = useQuery({
    queryKey: ['case', caseIdParam],
    queryFn:  () => casesApi.getById(caseIdParam),
    enabled:  !!caseIdParam && caseIdParam !== 'novo',
  });

  useEffect(() => {
    if (caseLoaded && caseData) loadFromCase(caseData);
  }, [caseLoaded, caseData]); // eslint-disable-line

  useEffect(() => {
    if (!caseIdParam || caseIdParam === 'novo') reset();
  }, [caseIdParam]); // eslint-disable-line

  useEffect(() => {
    const tab = searchParams.get('tab');
    const index = TABS.findIndex(item => item.id === tab);
    if (index >= 0) setActiveTab(index);
  }, [searchParams, setActiveTab]);

  const isPostApprovalStatus = LOCKED_FICHA_STATUSES.includes(caseData?.status);
  const canCompleteAfterApproval = user?.permissions?.includes(PERMISSIONS.FICHA_COMPLETE_AFTER_APPROVAL);
  const isFichaLocked = isPostApprovalStatus && !canCompleteAfterApproval;
  const canArchive = !!caseId && !!caseData && user?.permissions?.includes(PERMISSIONS.CASE_ARCHIVE);
  const canAddToCorpus = caseData?.status === 'protocolado'
    && user?.permissions?.includes(PERMISSIONS.PETITION_APPROVE);
  const canDelete = !!caseId
    && !!caseData
    && user?.role === 'admin'
    && user?.permissions?.includes(PERMISSIONS.FICHA_DELETE)
    && !PROTOCOLLED_OR_LATER_STATUSES.includes(caseData?.status);

  useEffect(() => {
    setLockMode(isFichaLocked);
  }, [isFichaLocked, setLockMode]);

  // Salvar rascunho
  const saveMutation = useMutation({
    mutationFn: async () => {
      const currentFormData = useFormStore.getState().formData;
      if (!caseId) {
        const nc = await formsApi.createWithCase(contactId, currentFormData);
        setCaseId(nc.id);
        navigate(`/ficha/${nc.id}`, { replace: true });
        return nc;
      }
      return formsApi.save(caseId, currentFormData);
    },
    onSuccess: () => toast.success('Ficha salva.'),
    onError:   (err) => toast.error(fichaSaveErrorMessage(err)),
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const startGenerationFlow = useMutation({
    mutationFn: async () => {
      let id = caseId;
      const currentFormData = useFormStore.getState().formData;
      if (!id) {
        const nc = await formsApi.createWithCase(contactId, currentFormData);
        id = nc.id;
        setCaseId(id);
        navigate(`/ficha/${id}`, { replace: true });
      } else {
        await formsApi.save(id, currentFormData);
      }
      return id;
    },
    onSuccess: () => {
      setShowConfirmModal(true);
    },
    onError: (err) => {
      toast.error(fichaSaveErrorMessage(err, 'Erro ao salvar rascunho antes da geração.'));
    },
  });

  const genMutation = useMutation({
    mutationFn: async () => {
      return petitionsApi.generate(caseId);
    },
    onMutate: () => {
      setIsGenerating(true);
      setShowConfirmModal(false);
      const toastId = toast.loading('Iniciando geração em segundo plano...');
      return { toastId };
    },
    onSuccess: (data, _vars, ctx) => {
      setIsGenerating(false);
      toast.dismiss(ctx?.toastId);
      if (data?.status === 'gerada_com_pendencias') {
        // Quality gate reprovou: o caso NÃO avançou para revisão, mas a petição ficou salva com o
        // relatório e o .docx. Quem decide o que fazer com ela é o painel no topo da ficha —
        // baixar e conferir, corrigir a ficha, ou assumir a pendência e mandar para revisão.
        queryClient.invalidateQueries({ queryKey: ['peticao-do-caso', caseId] });
        queryClient.invalidateQueries({ queryKey: ['perguntas-caso', caseId] });
        const itens = (data.quality_criticals || []).join('; ');
        toast.error(
          `Petição gerada, mas o controle de qualidade encontrou pendências críticas${itens ? `: ${itens}` : ''}. ` +
          'Veja o painel no topo da ficha para conferir a peça ou enviá-la assim mesmo.',
          { duration: 12000 },
        );
        return;
      }
      toast.success('Geração iniciada com sucesso! Acompanhe o progresso em Casos.', { duration: 6000 });
      navigate('/casos');
    },
    onError: (err, _vars, ctx) => {
      setIsGenerating(false);
      toast.dismiss(ctx?.toastId);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao iniciar geração', { duration: 6000 });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => caseData?.archived_at ? casesApi.unarchive(caseId) : casesApi.archive(caseId),
    onSuccess: (data) => {
      queryClient.setQueryData(['case', caseIdParam], data);
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success(data.archived_at ? 'Caso arquivado.' : 'Caso desarquivado.');
    },
    onError: (err) => toast.error(apiDetailMessage(err, 'Erro ao atualizar arquivamento.')),
  });

  const addToCorpusMutation = useMutation({
    mutationFn: () => casesApi.addToCorpus(caseId),
    onSuccess: (data) => {
      queryClient.setQueryData(['case', caseIdParam], data);
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Caso adicionado ao corpus de curadoria.');
    },
    onError: (err) => toast.error(apiDetailMessage(err, 'Erro ao adicionar caso ao corpus.')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => casesApi.remove(caseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
      toast.success('Ficha excluída.');
      reset();
      navigate('/casos');
    },
    onError: (err) => toast.error(apiDetailMessage(err, 'Erro ao excluir ficha.')),
  });

  function handleArchiveToggle() {
    const action = caseData?.archived_at ? 'desarquivar' : 'arquivar';
    if (window.confirm(`Confirmar ${action} este caso?`)) archiveMutation.mutate();
  }

  const ActiveTab = TABS[activeTab]?.component;

  if (isLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );

  return (
    <div className="ficha-layout">

      {/* ── FORMULÁRIO ── */}
      <div>
        {/* Título */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0 }}>{caseIdParam === 'novo' || !caseIdParam ? 'Nova Ficha de Atendimento' : 'Editar Ficha'}</h1>
            </div>
            <p style={{ marginTop: '.125rem' }}>
              Reclamação Trabalhista — {formData.comarca || <em>comarca não definida</em>}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem' }}>
            <div className="progress-bar" style={{ width: 72 }}>
              <div className="progress-fill" style={{ '--fill': completeness.pct / 100,
                background: completeness.pct >= 80 ? 'var(--success)' : completeness.pct >= 50 ? 'var(--warning)' : 'var(--error)' }} />
            </div>
            <span style={{ fontSize: '.8rem', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', minWidth: '2.5rem' }}>
              {completeness.pct}%
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '1.125rem' }}>
          <FichaActionToolbar
            archived={!!caseData?.archived_at}
            canAddToCorpus={canAddToCorpus}
            canArchive={canArchive}
            canDelete={canDelete}
            completenessPct={completeness.pct}
            corpusAdded={!!caseData?.corpus_added_at}
            isAddingToCorpus={addToCorpusMutation.isPending}
            isArchiving={archiveMutation.isPending}
            isDeleting={deleteMutation.isPending}
            isGenerating={isGenerating}
            isPreparingGeneration={startGenerationFlow.isPending}
            isSaving={saveMutation.isPending}
            onAddToCorpus={() => addToCorpusMutation.mutate()}
            onArchiveToggle={handleArchiveToggle}
            onDelete={() => setConfirmDeleteOpen(true)}
            onGenerate={() => startGenerationFlow.mutate()}
            onImport={handleImportClick}
            onPrint={() => navigate('/formulario/imprimir')}
            onSave={() => saveMutation.mutate()}
          />
        </div>

        {completeness.pct < 50 && completeness.missing.length > 0 && (
          <div className="missing-wrap" style={{ marginBottom: '1.125rem' }}>
            <div className="missing-title"><AlertTriangle size={13} /> Campos obrigatórios faltando para gerar</div>
            <div className="missing-tags">
              {completeness.missing.map(f => <span key={f} className="missing-tag">{fieldLabel(f)}</span>)}
            </div>
          </div>
        )}

        {/* Card principal */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <SaveStatus status={saveStatus} />

          {/* Abas */}
          <div style={{ padding: '0 1.375rem', borderBottom: '1px solid var(--border-light)' }}>
            <div className="tabs-nav">
              {TABS.map((t, i) => {
                const Icon = t.icon;
                return (
                  <button key={t.id} className={`tab-btn${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
                    <Icon size={13} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Conteúdo */}
          <div style={{ padding: '1.375rem' }}>
            {isFichaLocked && (
              <div className="alert alert-warning" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '.625rem' }}>
                <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <strong>Ficha travada para protocolo.</strong>
                  <p style={{ margin: '.125rem 0 0' }}>Campos já preenchidos não podem ser alterados. Campos vazios seguem disponíveis para complemento.</p>
                </div>
              </div>
            )}
            {isPostApprovalStatus && canCompleteAfterApproval && (
              <div className="alert alert-info" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '.625rem' }}>
                <CheckCircle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                <div>
                  <strong>Edição excepcional habilitada.</strong>
                  <p style={{ margin: '.125rem 0 0' }}>Alterações em campos já preenchidos serão registradas em auditoria.</p>
                </div>
              </div>
            )}
            {caseId && (
              <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* A falha e a peça reprovada vêm primeiro: é o que o usuário acabou de tentar fazer. */}
                <AvisoFalhaDaGeracao caseId={caseId} caseStatus={caseData?.status} />
                <PeticaoReprovadaPanel caseId={caseId} clientName={caseData?.client_name} />
                <PerguntasPanel caseId={caseId} />
              </div>
            )}
            {ActiveTab && <ActiveTab />}
            <CustomFieldsBlock tab={TABS[activeTab]?.id} />
          </div>

          {/* Navegação por abas */}
          <div style={{ padding: '.875rem 1.375rem', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg)' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => handleTabChange(Math.max(0, activeTab - 1))} disabled={activeTab === 0}>
              <ChevronLeft size={13} /> Anterior
            </button>
            <div className="dot-nav">
              {TABS.map((t, i) => (
                <button
                  key={t.id}
                  type="button"
                  className={`dot${activeTab === i ? ' active' : ''}`}
                  aria-label={`Aba ${i + 1} de ${TABS.length}: ${t.label}`}
                  aria-current={activeTab === i ? 'true' : undefined}
                  onClick={() => handleTabChange(i)}
                />
              ))}
            </div>
            {activeTab < TABS.length - 1
              ? <button className="btn btn-ghost btn-sm" onClick={() => handleTabChange(Math.min(TABS.length - 1, activeTab + 1))}>Próxima <ChevronRight size={13} /></button>
              : <div style={{ width: 80 }} />
            }
          </div>
        </div>

        {showConfirmModal && (
          <ConfirmGenerationModal
            caseId={caseId}
            onClose={() => setShowConfirmModal(false)}
            onConfirm={() => genMutation.mutate()}
            submitting={genMutation.isPending}
          />
        )}
        <ConfirmModal
          isOpen={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          onConfirm={() => deleteMutation.mutate()}
          title="Excluir ficha"
          message="Esta ação remove a ficha e o caso antes do protocolo. Ela não pode ser desfeita."
          confirmLabel="Excluir"
          danger
        />
      </div>

      <CintraFloatingWidget caseId={caseId} />

      {importDrawerOpen && (
        <ImportDrawer 
          caseId={caseId} 
          onClose={() => setImportDrawerOpen(false)} 
        />
      )}
    </div>
  );
}

export function CintraFloatingWidget({ caseId }) {
  // 'closed' desmonta o painel (histórico recarrega do backend ao reabrir);
  // 'minimized' mantém montado só escondido (preserva rascunho não enviado).
  const [state, setState] = useState('closed');
  const [buttonPosition, setButtonPosition] = useState(null);
  const dragRef = React.useRef(null);
  const suppressClickRef = React.useRef(false);

  const handleFloatPointerDown = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleFloatPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const padding = 8;
    const maxLeft = Math.max(padding, window.innerWidth - drag.width - padding);
    const maxTop = Math.max(padding, window.innerHeight - drag.height - padding);
    const left = Math.min(Math.max(event.clientX - drag.offsetX, padding), maxLeft);
    const top = Math.min(Math.max(event.clientY - drag.offsetY, padding), maxTop);

    if (Math.abs(event.clientX - drag.startX) > 4 || Math.abs(event.clientY - drag.startY) > 4) {
      drag.moved = true;
    }

    setButtonPosition({ left, top });
    event.preventDefault();
  };

  const handleFloatPointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    suppressClickRef.current = drag.moved;
    if (drag.moved) {
      window.setTimeout(() => { suppressClickRef.current = false; }, 0);
    }
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handleFloatClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setState('open');
  };

  return (
    <>
      {state !== 'open' && (
        <button
          type="button"
          className="cintra-float-btn"
          style={buttonPosition ? {
            left: `${buttonPosition.left}px`,
            top: `${buttonPosition.top}px`,
            right: 'auto',
            bottom: 'auto',
          } : undefined}
          onPointerDown={handleFloatPointerDown}
          onPointerMove={handleFloatPointerMove}
          onPointerUp={handleFloatPointerUp}
          onPointerCancel={() => { dragRef.current = null; }}
          onClick={handleFloatClick}
          aria-label={state === 'minimized' ? 'Reabrir a Cintra IA' : 'Abrir a Cintra IA'}
          title="Arraste para mover"
        >
          <Bot size={24} />
        </button>
      )}
      {state !== 'closed' && (
        <div className="cintra-drawer" style={{ display: state === 'minimized' ? 'none' : 'flex' }}>
          <AIAuditPanel caseId={caseId} onMinimize={() => setState('minimized')} onClose={() => setState('closed')} />
        </div>
      )}
    </>
  );
}

function AIAuditPanel({ caseId, onMinimize, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const messagesEndRef = React.useRef(null);

  // Estados do Diff/Import no chat
  const [diffs, setDiffs] = useState(null);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const fileInputRef = React.useRef(null);
  
  const { formData, setFields } = useFormStore();

  useEffect(() => {
    if (caseId) {
      loadChatHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  async function loadChatHistory() {
    setLoadingHistory(true);
    setErrorMsg('');
    try {
      const history = await formsApi.getChatHistory(caseId);
      setMessages(history);
    } catch (err) {
      setErrorMsg('Erro ao carregar o histórico de mensagens.');
    } finally {
      setLoadingHistory(false);
    }
  }

  async function handleSendMessage(text) {
    if (text && typeof text !== 'string' && typeof text.preventDefault === 'function') {
      text.preventDefault();
    }
    const textToSend = typeof text === 'string' ? text : inputText;
    if (!textToSend.trim()) return;

    if (typeof text !== 'string') {
      setInputText('');
    }
    setChatLoading(true);
    setErrorMsg('');

    // Mensagem temporária do usuário
    const tempUserMsg = {
      id: 'temp_user_' + Date.now(),
      role: 'user',
      content: textToSend,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await formsApi.sendChatMessage(caseId, textToSend);
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        return [...filtered, { id: response.id + '_user', role: 'user', content: textToSend, created_at: tempUserMsg.created_at }, response];
      });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao enviar mensagem';
      setErrorMsg(detail);
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      // Devolve o texto pro input em vez de descartar — evita perder uma pergunta já digitada
      // por causa de uma falha de rede. Só se veio do campo de texto (não de uma ação rápida).
      if (typeof text !== 'string') {
        setInputText(textToSend);
      }
    } finally {
      setChatLoading(false);
    }
  }

  // Enviar arquivo pelo chat
  async function handleFileAttach(e) {
    const file = e.target.files?.[0];
    if (!file || !confirmarEnvioDeImagem(file)) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setFileLoading(true);
    setErrorMsg('');
    try {
      const data = await formsApi.importDocument(caseId, file);
      const importedFicha = data?.ficha_json || {};

      // Gera os diffs a partir de importedFicha
      const diffList = generateDiffs(importedFicha);

      if (diffList.length === 0) {
        toast.success('Documento processado! Todos os dados já coincidem com a ficha atual.');
      } else {
        setDiffs(diffList);
        setShowDiffModal(true);
      }

      // Adiciona mensagens no chat avisando sobre a importação
      const fileMsgUser = {
        id: 'file_user_' + Date.now(),
        role: 'user',
        content: `Enviei o arquivo: ${file.name}`,
        created_at: new Date().toISOString()
      };
      const fileMsgAssistant = {
        id: 'file_assistant_' + Date.now(),
        role: 'assistant',
        content: `Documento "${file.name}" importado com sucesso. Clique em "Revisar Alterações" para aplicar as informações sugeridas à ficha.`,
        action_type: 'import',
        action_metadata: { imported_ficha: importedFicha },
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, fileMsgUser, fileMsgAssistant]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao importar documento';
      setErrorMsg(detail);
    } finally {
      setFileLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function generateDiffs(importedFicha) {
    const diffList = [];
    Object.entries(importedFicha).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((aiItem, index) => {
          const isNew = !!aiItem.is_new;
          const matchedId = aiItem.matched_id;
          
          let currentItem = null;
          if (matchedId) {
            currentItem = (formData[key] || []).find(item => item.id === matchedId);
          }

          const fieldsDiff = [];
          Object.entries(aiItem).forEach(([subKey, subVal]) => {
            if (subKey === 'is_new' || subKey === 'matched_id' || subKey === 'id') return;
            
            const curVal = currentItem ? currentItem[subKey] : undefined;
            const normCur = (curVal === null || curVal === undefined) ? '' : String(curVal).trim();
            const normAi = (subVal === null || subVal === undefined) ? '' : String(subVal).trim();

            if (normAi !== '' && normAi !== normCur) {
              fieldsDiff.push({
                key: subKey,
                label: fieldLabel(subKey),
                currentValue: normCur || '(vazio)',
                aiValue: normAi
              });
            }
          });

          if (isNew || fieldsDiff.length > 0) {
            diffList.push({
              type: 'item_card',
              arrayName: key,
              isNew,
              matchedId,
              title: ARRAY_DIFF_TITLES[key] || fieldLabel(key),
              subtitle: aiItem.name || aiItem.contracted_role || aiItem.number || `Item #${index + 1}`,
              fields: fieldsDiff,
              rawItem: aiItem,
              selected: true
            });
          }
        });
      } else {
        if (key === 'pedidos' && typeof value === 'object' && value !== null) {
          Object.entries(value).forEach(([subKey, subVal]) => {
            if (!subVal) return;
            const curVal = formData.pedidos?.[subKey];
            const normCurValor = curVal?.valor ? String(curVal.valor).trim() : '';
            const normAiValor = subVal.valor ? String(subVal.valor).trim() : '';
            const normCurEnabled = curVal?.enabled || false;
            const normAiEnabled = subVal.enabled || false;
            
            if (normAiValor !== normCurValor || normAiEnabled !== normCurEnabled) {
              const defItem = PEDIDOS_DEF.find(p => p.id === subKey);
              const label = defItem ? rotuloDoPedido(defItem, formData) : subKey;
              
              diffList.push({
                type: 'flat',
                key: `pedidos.${subKey}`,
                fieldName: `Pedido: ${label}`,
                currentValue: normCurEnabled ? `Sim (R$ ${normCurValor})` : 'Não selecionado',
                aiValue: normAiEnabled ? `Sim (R$ ${normAiValor})` : 'Não selecionado',
                rawValue: subVal,
                selected: true
              });
            }
          });
          return;
        }
        if (typeof value === 'object' && value !== null) return;
        
        const currentValue = formData[key];
        const normCurrent = (currentValue === null || currentValue === undefined) ? '' : String(currentValue).trim();
        const normAi = (value === null || value === undefined) ? '' : String(value).trim();
        
        if (normAi !== '' && normAi !== normCurrent) {
          diffList.push({
            type: 'flat',
            key,
            fieldName: fieldLabel(key),
            currentValue: normCurrent || '(vazio)',
            aiValue: normAi,
            rawValue: value,
            selected: true
          });
        }
      }
    });
    return diffList;
  }

  const handleConfirmMerge = async (selectedItems) => {
    const selectedFields = selectedItems.map(item => (
      item.type === 'flat'
        ? { key: item.key, rawValue: item.rawValue }
        : { key: item.arrayName, rawValue: [item.rawItem] }
    ));
    const updatedFormData = mergeImportedFields(formData, selectedFields);

    const toastId = toast.loading('Mesclando e salvando alterações no servidor...');
    try {
      await formsApi.save(caseId, updatedFormData);
      setFields(updatedFormData);
      toast.success('Dados mesclados e ficha salva com sucesso!', { id: toastId });
      setShowDiffModal(false);
      setDiffs(null);
    } catch (err) {
      toast.error(fichaSaveErrorMessage(err, 'Erro ao salvar ficha mesclada.'), { id: toastId });
    }
  };

  const [activeAuditTab, setActiveAuditTab] = useState('reclamante');
  const tabsInfo = {
    reclamante: 'Reclamante',
    reclamado: 'Reclamado',
    contrato: 'Contrato',
    jornada: 'Jornada',
  };

  function handleCopyQuestion(text) {
    navigator.clipboard.writeText(text);
    toast.success('Pergunta copiada para a área de transferência!');
  }

  function renderAuditMessage(msg) {
    const auditData = msg.action_metadata;
    if (!auditData || !auditData.tabs) {
      return <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>;
    }

    const currentTabData = auditData.tabs[activeAuditTab] || { inconsistencies: [], missing_fields: [], suggested_questions: [] };
    const totals = Object.values(auditData.tabs).reduce((acc, t) => ({
      inconsistencies: acc.inconsistencies + (t?.inconsistencies?.length || 0),
      missing_fields: acc.missing_fields + (t?.missing_fields?.length || 0),
      suggested_questions: acc.suggested_questions + (t?.suggested_questions?.length || 0),
    }), { inconsistencies: 0, missing_fields: 0, suggested_questions: 0 });
    const hasResults = totals.inconsistencies + totals.missing_fields + totals.suggested_questions > 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', color: 'var(--text-primary)' }}>
        <div style={{ fontSize: '.72rem', color: 'var(--text-secondary)', marginBottom: '.25rem' }}>{msg.content}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', background: 'var(--bg-card)', padding: '.5rem .75rem', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: '.75rem', fontWeight: 'bold', color: 'var(--text-primary)', marginBottom: '.25rem' }}>
            Auditoria Completa:
          </div>
          {hasResults && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.35rem', fontSize: '.65rem' }}>
              {totals.inconsistencies > 0 && (
                <span style={{ background: 'var(--warning-bg)', color: 'var(--warning)', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                  {totals.inconsistencies} inconsistência{totals.inconsistencies > 1 ? 's' : ''}
                </span>
              )}
              {totals.missing_fields > 0 && (
                <span style={{ background: 'var(--info-bg)', color: 'var(--info)', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                  {totals.missing_fields} campo{totals.missing_fields > 1 ? 's' : ''} faltando
                </span>
              )}
              {totals.suggested_questions > 0 && (
                <span style={{ background: 'var(--gold-pale)', color: 'var(--gold-dark)', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>
                  {totals.suggested_questions} pergunta{totals.suggested_questions > 1 ? 's' : ''} sugerida{totals.suggested_questions > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
          {!hasResults ? (
            <div style={{ fontSize: '.72rem', color: 'var(--success)', padding: '.25rem' }}>
              ✓ Nenhuma inconsistência identificada pela IA!
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '.25rem', overflowX: 'auto', paddingBottom: '.25rem', borderBottom: '1px solid var(--border-light)' }}>
                {Object.keys(tabsInfo).map(tabKey => {
                  const tData = auditData.tabs[tabKey];
                  const count = (tData?.inconsistencies?.length || 0) + (tData?.missing_fields?.length || 0) + (tData?.suggested_questions?.length || 0);

                  return (
                    <button
                      key={tabKey}
                      type="button"
                      className={`cintra-audit-tab${activeAuditTab === tabKey ? ' active' : ''}`}
                      onClick={(e) => { e.preventDefault(); setActiveAuditTab(tabKey); }}
                      style={{
                        padding: '.2rem .4rem',
                        fontSize: '.65rem',
                        fontWeight: 600,
                        borderRadius: '4px',
                        border: '1px solid',
                        borderColor: activeAuditTab === tabKey ? 'var(--gold)' : 'var(--border-light)',
                        background: activeAuditTab === tabKey ? 'var(--gold-pale)' : undefined,
                        color: activeAuditTab === tabKey ? 'var(--text-primary)' : 'var(--text-secondary)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tabsInfo[tabKey]} ({count})
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', maxHeight: 200, overflowY: 'auto', paddingRight: '.2rem' }}>
                {currentTabData.inconsistencies?.map((inc, i) => (
                  <div key={i} className="alert alert-warning" style={{ padding: '.4rem .6rem', gap: '.35rem' }}>
                    <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '.7rem', color: 'var(--text-primary)' }}>{inc}</span>
                  </div>
                ))}

                {currentTabData.missing_fields?.map((mis, i) => (
                  <div key={i} className="alert alert-info" style={{ padding: '.4rem .6rem', gap: '.35rem' }}>
                    <FileText size={12} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ fontSize: '.7rem', color: 'var(--text-primary)' }}>{fieldLabel(mis)}</span>
                  </div>
                ))}

                {currentTabData.suggested_questions?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', marginTop: '.25rem' }}>
                    <div style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--text-primary)' }}>Perguntas Sugeridas:</div>
                    {currentTabData.suggested_questions.map((q, i) => (
                      <div key={i} style={{ background: 'var(--bg)', padding: '.4rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-light)', position: 'relative' }}>
                        <div style={{ fontSize: '.7rem', color: 'var(--text-primary)', paddingRight: '1.25rem', fontWeight: 500 }}>
                          {q.question}
                        </div>
                        {q.context && (
                          <div style={{ fontSize: '.65rem', color: 'var(--text-muted)', marginTop: '.15rem', fontStyle: 'italic' }}>
                            Por quê: {q.context}
                          </div>
                        )}
                        <button
                          type="button"
                          className="cintra-icon-btn"
                          onClick={() => handleCopyQuestion(q.question)}
                          title="Copiar pergunta"
                          aria-label="Copiar pergunta para a área de transferência"
                          style={{ position: 'absolute', top: '.15rem', right: '.15rem', padding: '.2rem' }}
                        >
                          <Copy size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  function renderActionMessage(msg) {
    if (msg.action_type === 'audit') {
      return renderAuditMessage(msg);
    }
    
    const isImport = msg.action_type === 'import';
    const isCalculo = msg.action_type === 'calculo';
    
    if (isImport || isCalculo) {
      const data = msg.action_metadata;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', color: 'var(--text-primary)' }}>
          <div style={{ fontSize: '.72rem' }}>{msg.content}</div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ alignSelf: 'flex-start', marginTop: '.25rem', borderColor: 'var(--gold)', color: 'var(--text-primary)' }}
            onClick={() => {
              const diffList = generateDiffs(data.imported_ficha || data.calculo_ficha || {});
              if (diffList.length === 0) {
                toast.success('Todos os dados já coincidem com a ficha atual.');
              } else {
                setDiffs(diffList);
                setShowDiffModal(true);
              }
            }}
          >
            Revisar Alterações
          </button>
        </div>
      );
    }

    return <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>;
  }

  const avatarStyle = {
    width: 32, height: 32, borderRadius: '50%',
    background: 'var(--gold)', color: 'var(--navy)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };

  const headerControls = (onMinimize || onClose) && (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.15rem', marginLeft: '.25rem' }}>
      {onMinimize && (
        <button type="button" className="cintra-icon-btn" onClick={onMinimize} aria-label="Minimizar Cintra IA" title="Minimizar">
          <Minus size={15} />
        </button>
      )}
      {onClose && (
        <button type="button" className="cintra-icon-btn" onClick={onClose} aria-label="Fechar Cintra IA" title="Fechar">
          <X size={15} />
        </button>
      )}
    </div>
  );

  if (!caseId) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.75rem 1rem', borderBottom: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <div style={avatarStyle}><Bot size={17} /></div>
            <span style={{ fontSize: '.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>Cintra IA</span>
          </div>
          {headerControls}
        </div>
        <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', margin: '0.75rem 1rem 0' }}>
          Salve o rascunho da ficha para habilitar a Cintra IA.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Cabeçalho do Chat */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.625rem .875rem', borderBottom: '1px solid var(--border-light)', background: 'var(--bg-card)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.55rem' }}>
          <div style={avatarStyle}><Bot size={17} /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            <span style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>Cintra IA</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '.3rem', fontSize: '.65rem', color: 'var(--text-muted)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
              Assistente do caso
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => handleSendMessage("Auditar Ficha com IA")}
            disabled={chatLoading}
            title="Auditar ficha com IA"
            style={{ padding: '.3rem .55rem', fontSize: '.7rem', gap: '.25rem' }}
          >
            <Zap size={11} /> Auditar
          </button>
          {headerControls}
        </div>
      </div>

      {/* Corpo de Mensagens */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '.75rem', background: 'var(--bg)' }}>
        {loadingHistory ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '.5rem' }}>
            <div className="spinner" style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>Carregando conversa...</span>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '.5rem', color: 'var(--text-muted)', textAlign: 'center', padding: '0 1rem' }}>
            <Bot size={24} style={{ color: 'var(--border)', marginBottom: '.25rem' }} />
            <div style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-primary)' }}>Olá! Eu sou a Cintra IA.</div>
            <div style={{ fontSize: '.68rem' }}>Me diga o que aconteceu no caso ou peça um próximo passo. Ex: “o que falta para horas extras?” ou “quais perguntas faço ao cliente?”</div>
          </div>
        ) : (
          messages.map((msg) => {
            const isUser = msg.role === 'user';
            return (
              <div
                key={msg.id}
                style={{
                  alignSelf: isUser ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: isUser ? 'var(--navy)' : 'var(--bg-card)',
                  color: isUser ? '#fff' : 'var(--text-primary)',
                  padding: '.5rem .75rem',
                  borderRadius: isUser ? '8px 8px 0px 8px' : '8px 8px 8px 0px',
                  border: isUser ? 'none' : '1px solid var(--border-light)',
                  boxShadow: 'var(--shadow-sm)',
                  fontSize: '.72rem',
                  lineHeight: '1.4'
                }}
              >
                {isUser ? <div>{msg.content}</div> : renderActionMessage(msg)}
              </div>
            );
          })
        )}

        {chatLoading && (
          <div
            style={{
              alignSelf: 'flex-start',
              background: 'var(--bg-card)',
              padding: '.5rem .75rem',
              borderRadius: '8px 8px 8px 0px',
              border: '1px solid var(--border-light)',
              boxShadow: 'var(--shadow-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: '.4rem'
            }}
          >
            <div className="spinner" style={{ width: 12, height: 12 }} />
            <span style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>Cintra IA está pensando...</span>
          </div>
        )}

        {fileLoading && (
          <div
            style={{
              alignSelf: 'flex-start',
              background: 'var(--bg-card)',
              padding: '.5rem .75rem',
              borderRadius: '8px 8px 8px 0px',
              border: '1px solid var(--border-light)',
              boxShadow: 'var(--shadow-sm)',
              display: 'flex',
              alignItems: 'center',
              gap: '.4rem'
            }}
          >
            <div className="spinner" style={{ width: 12, height: 12 }} />
            <span style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>Processando arquivo...</span>
          </div>
        )}

        {errorMsg && (
          <div style={{ alignSelf: 'center', padding: '.4rem .6rem', background: 'var(--error-bg)', color: 'var(--error)', fontSize: '.68rem', borderRadius: '4px', border: '1px solid var(--border-error)' }}>
            {errorMsg}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Rodapé do Chat - Input & Envio */}
      <form
        onSubmit={handleSendMessage}
        style={{
          display: 'flex',
          gap: '.5rem',
          padding: '.5rem .75rem',
          borderTop: '1px solid var(--border-light)',
          background: 'var(--bg-card)',
          alignItems: 'center'
        }}
      >
        <button
          type="button"
          className="cintra-icon-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={chatLoading || fileLoading}
          title="Anexar documento ou foto (DOC, DOCX, PDF, PNG, JPG, WEBP)"
          aria-label="Anexar documento"
        >
          {fileLoading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Paperclip size={16} />}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={IMPORT_ACCEPT}
          onChange={handleFileAttach}
          style={{ display: 'none' }}
        />

        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={chatLoading || fileLoading}
          placeholder={fileLoading ? 'Processando arquivo...' : chatLoading ? 'Enviando...' : 'Pergunte sobre a ficha ou próximo passo...'}
          style={{
            flex: 1,
            border: '1px solid var(--border-light)',
            borderRadius: '4px',
            padding: '.4rem .6rem',
            fontSize: '.75rem',
            background: 'var(--bg-input)',
            outline: 'none',
            transition: 'border-color 0.15s ease'
          }}
        />

        <button
          type="submit"
          disabled={chatLoading || fileLoading || !inputText.trim()}
          aria-label="Enviar mensagem"
          style={{
            border: 'none',
            background: inputText.trim() ? 'var(--gold)' : 'transparent',
            color: inputText.trim() ? 'var(--navy)' : 'var(--text-muted)',
            borderRadius: '4px',
            padding: '.4rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s ease'
          }}
        >
          {chatLoading ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={12} />}
        </button>
      </form>

      {showDiffModal && diffs && (
        <DiffMergeModal
          diffs={diffs}
          onClose={() => { setShowDiffModal(false); setDiffs(null); }}
          onConfirm={handleConfirmMerge}
        />
      )}
    </div>
  );
}

function ImportDrawer({ caseId, onClose }) {
  const { formData, setFields } = useFormStore();
  const [loading, setLoading] = useState(false);
  const [diffs, setDiffs] = useState(null);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleDownloadModelo = async () => {
    try {
      triggerBlobDownload(await formsApi.downloadModeloFicha(), 'modelo-ficha-peticionadorbr.docx');
    } catch {
      toast.error('Erro ao baixar o modelo de ficha');
    }
  };

  const processFile = async (file) => {
    if (!file || !confirmarEnvioDeImagem(file)) return;

    setLoading(true);
    try {
      const data = await formsApi.importDocument(caseId, file);
      const importedFicha = data?.ficha_json || {};
      
      const diffList = [];

      Object.entries(importedFicha).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((aiItem, index) => {
            const isNew = !!aiItem.is_new;
            const matchedId = aiItem.matched_id;
            
            let currentItem = null;
            if (matchedId) {
              currentItem = (formData[key] || []).find(item => item.id === matchedId);
            }

            const fieldsDiff = [];
            Object.entries(aiItem).forEach(([subKey, subVal]) => {
              if (subKey === 'is_new' || subKey === 'matched_id' || subKey === 'id') return;
              
              const curVal = currentItem ? currentItem[subKey] : undefined;
              const normCur = (curVal === null || curVal === undefined) ? '' : String(curVal).trim();
              const normAi = (subVal === null || subVal === undefined) ? '' : String(subVal).trim();

              if (normAi !== '' && normAi !== normCur) {
                fieldsDiff.push({
                  key: subKey,
                  label: fieldLabel(subKey),
                  currentValue: normCur || '(vazio)',
                  aiValue: normAi
                });
              }
            });

            if (isNew || fieldsDiff.length > 0) {
              diffList.push({
                type: 'item_card',
                arrayName: key,
                isNew,
                matchedId,
                title: ARRAY_DIFF_TITLES[key] || fieldLabel(key),
                subtitle: aiItem.name || aiItem.contracted_role || aiItem.number || `Item #${index + 1}`,
                fields: fieldsDiff,
                rawItem: aiItem,
                selected: true
              });
            }
          });
        } else {
          if (typeof value === 'object' && value !== null) return;
          
          const currentValue = formData[key];
          const normCurrent = (currentValue === null || currentValue === undefined) ? '' : String(currentValue).trim();
          const normAi = (value === null || value === undefined) ? '' : String(value).trim();
          
          if (normAi !== '' && normAi !== normCurrent) {
            diffList.push({
              type: 'flat',
              key,
              fieldName: fieldLabel(key),
              currentValue: normCurrent || '(vazio)',
              aiValue: normAi,
              rawValue: value,
              selected: true
            });
          }
        }
      });

      if (diffList.length === 0) {
        toast.success('Documento processado! Todos os dados já coincidem com a ficha atual.');
      } else {
        setDiffs(diffList);
        setShowDiffModal(true);
      }
    } catch (err) {
      toast.error(apiDetailMessage(err, 'Erro ao extrair dados do documento.'));
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    e.target.value = '';  // permite reenviar o mesmo arquivo depois de cancelar a confirmação
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleConfirmMerge = async (selectedItems) => {
    const selectedFields = selectedItems.map(item => (
      item.type === 'flat'
        ? { key: item.key, rawValue: item.rawValue }
        : { key: item.arrayName, rawValue: [item.rawItem] }
    ));
    const updatedFormData = mergeImportedFields(formData, selectedFields);

    const toastId = toast.loading('Mesclando e salvando alterações no servidor...');
    try {
      await formsApi.save(caseId, updatedFormData);
      setFields(updatedFormData);
      toast.success('Dados mesclados e ficha salva com sucesso!', { id: toastId });
      setShowDiffModal(false);
      setDiffs(null);
      onClose();
    } catch (err) {
      toast.error(fichaSaveErrorMessage(err, 'Erro ao salvar ficha mesclada.'), { id: toastId });
    }
  };

  return (
    <>
      <div className="import-drawer-overlay" onClick={onClose} />
      <div className="import-drawer">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-light)', paddingBottom: '.5rem' }}>
          <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '.4rem', margin: 0 }}>
            <Bot size={16} color="var(--gold)" />
            Importação com IA
          </h2>
          <button 
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.25rem', padding: 0 }}
          >
            ×
          </button>
        </div>

        <p style={{ fontSize: '.78rem', color: 'var(--text-secondary)', margin: 0 }}>
          Carregue DOC, DOCX, PDF, foto ou digitalização (PNG/JPG/WEBP) para extrair dados da ficha.
          Documentos preenchidos no <strong>modelo oficial</strong> são lidos campo a campo, sem
          depender da IA — mais fiel e sem custo de tokens.
        </p>

        <button
          type="button"
          className="btn btn-outline btn-sm"
          style={{ alignSelf: 'flex-start' }}
          onClick={handleDownloadModelo}
        >
          <FileText size={13} /> Baixar modelo de ficha (Word)
        </button>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '.75rem', padding: '2rem 0' }}>
            <div className="spinner" style={{ width: 24, height: 24 }} />
            <span style={{ fontSize: '.78rem', color: 'var(--text-muted)', textAlign: 'center', fontWeight: 500 }}>
              Analisando documento com IA...
            </span>
            <span style={{ fontSize: '.68rem', color: 'var(--text-muted)', textAlign: 'center' }}>
              Isso pode levar de 15 a 30 segundos
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1, justifyContent: 'center' }}>
            <div 
              style={{
                border: isDragging ? '2px dashed var(--gold)' : '2px dashed var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: '2.5rem 1.5rem',
                textAlign: 'center',
                background: isDragging ? 'var(--gold-pale)' : 'var(--bg)',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload size={32} style={{ color: 'var(--gold)', marginBottom: '.75rem', opacity: 0.8 }} />
              <div style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '.25rem' }}>
                Clique ou arraste o arquivo aqui
              </div>
              <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>
                DOC, DOCX, PDF, PNG, JPG ou WEBP de até 10MB
              </div>
            </div>
            
            <input 
              ref={fileInputRef}
              type="file"
              accept={IMPORT_ACCEPT}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border-light)', paddingTop: '.75rem' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={loading}>
            Fechar Gaveta
          </button>
        </div>
      </div>

      {showDiffModal && diffs && (
        <DiffMergeModal
          diffs={diffs}
          onClose={() => { setShowDiffModal(false); setDiffs(null); }}
          onConfirm={handleConfirmMerge}
        />
      )}
    </>
  );
}

function DiffMergeModal({ diffs, onClose, onConfirm }) {
  const [items, setItems] = useState(diffs);

  const toggleSelect = (index) => {
    setItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, selected: !item.selected } : item
    ));
  };

  const selectAll = (val) => {
    setItems(prev => prev.map(item => ({ ...item, selected: val })));
  };

  const handleMerge = () => {
    const selected = items.filter(i => i.selected);
    if (selected.length === 0) {
      toast.error('Selecione ao menos um item ou campo para mesclar.');
      return;
    }
    onConfirm(selected);
  };

  const flatItems = items.filter(i => i.type === 'flat');
  const cardItems = items.filter(i => i.type === 'item_card');

  return (
    <div 
      style={{ 
        position: 'fixed', 
        inset: 0, 
        background: 'rgba(26,35,50,0.5)', 
        zIndex: 2000, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div 
        className="card" 
        style={{ 
          width: '100%', 
          maxWidth: '720px', 
          maxHeight: '88vh', 
          display: 'flex', 
          flexDirection: 'column', 
          padding: '1.25rem',
          boxShadow: 'var(--shadow-lg)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-light)', paddingBottom: '.5rem', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '.5rem', margin: 0 }}>
            <Bot size={18} color="var(--gold)" />
            Revisão e Mesclagem de Dados da IA
          </h2>
          <button 
            onClick={onClose} 
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.5rem', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <p style={{ fontSize: '.8rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Abaixo estão as sugestões extraídas pela IA. Selecione quais deseja mesclar no formulário:
        </p>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '.75rem', fontSize: '.75rem' }}>
          <button className="btn btn-ghost btn-xs" onClick={() => selectAll(true)} style={{ padding: '.2rem .5rem' }}>Marcar todos</button>
          <button className="btn btn-ghost btn-xs" onClick={() => selectAll(false)} style={{ padding: '.2rem .5rem' }}>Desmarcar todos</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem', paddingRight: '.25rem', marginBottom: '1.25rem' }}>
          
          {flatItems.length > 0 && (
            <div>
              <h3 style={{ fontSize: '.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '.5rem', fontWeight: 700 }}>
                Informações Gerais
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                {items.map((item, idx) => {
                  if (item.type !== 'flat') return null;
                  return (
                    <div 
                      key={item.key}
                      onClick={() => toggleSelect(idx)}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '.5rem .75rem',
                        cursor: 'pointer',
                        background: item.selected ? 'rgba(200, 144, 42, 0.03)' : 'transparent',
                        borderColor: item.selected ? 'var(--gold)' : 'var(--border)',
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: '.75rem',
                        alignItems: 'center',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <input 
                        type="checkbox" 
                        checked={item.selected} 
                        onChange={() => {}}
                        style={{ cursor: 'pointer' }}
                      />
                      <div>
                        <div style={{ fontSize: '.725rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                          {item.fieldName}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '.5rem', alignItems: 'center', marginTop: '.2rem' }}>
                          <div style={{ fontSize: '.8rem', color: 'var(--text-secondary)', background: 'var(--bg)', padding: '.2rem .4rem', borderRadius: '4px', border: '1px solid var(--border-light)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.currentValue}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '.75rem' }}>→</div>
                          <div style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-primary)', background: 'rgba(46, 204, 113, 0.08)', padding: '.2rem .4rem', borderRadius: '4px', border: '1px solid rgba(46, 204, 113, 0.2)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.aiValue}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {cardItems.length > 0 && (
            <div>
              <h3 style={{ fontSize: '.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '.5rem', fontWeight: 700 }}>
                Estruturas e Listas (Cards)
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {items.map((item, idx) => {
                  if (item.type !== 'item_card') return null;
                  return (
                    <div 
                      key={`${item.arrayName}-${idx}`}
                      onClick={() => toggleSelect(idx)}
                      style={{
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '.85rem',
                        cursor: 'pointer',
                        background: item.selected ? 'rgba(200, 144, 42, 0.02)' : 'var(--bg)',
                        borderColor: item.selected ? 'var(--gold)' : 'var(--border)',
                        boxShadow: 'var(--shadow-sm)',
                        transition: 'all 0.15s ease',
                        position: 'relative'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem', marginBottom: '.65rem' }}>
                        <input 
                          type="checkbox" 
                          checked={item.selected} 
                          onChange={() => {}}
                          style={{ cursor: 'pointer' }}
                        />
                        <div>
                          <span style={{ fontSize: '.725rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', marginRight: '.5rem' }}>
                            {item.title}
                          </span>
                          <span className={`badge ${item.isNew ? 'badge-success' : 'badge-gold'}`} style={{ fontSize: '.65rem' }}>
                            {item.isNew ? 'Novo Item' : 'Edição'}
                          </span>
                          <div style={{ fontSize: '.85rem', fontWeight: 600, color: 'var(--text-primary)', marginTop: '.15rem' }}>
                            {item.subtitle}
                          </div>
                        </div>
                      </div>

                      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '.65rem', display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                        {item.isNew ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' }}>
                            {Object.entries(item.rawItem)
                              .filter(([k, v]) => k !== 'id' && k !== 'is_new' && k !== 'matched_id' && v)
                              .map(([k, v]) => (
                                <div key={k} style={{ fontSize: '.75rem' }}>
                                  <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{fieldLabel(k)}:</span>{' '}
                                  <strong style={{ color: 'var(--text-primary)' }}>{String(v)}</strong>
                                </div>
                              ))}
                          </div>
                        ) : (
                          item.fields.map(f => (
                            <div key={f.key} style={{ display: 'grid', gridTemplateColumns: '100px 1fr auto 1fr', gap: '.5rem', alignItems: 'center', fontSize: '.75rem' }}>
                              <span style={{ color: 'var(--text-muted)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {f.label}:
                              </span>
                              <span style={{ color: 'var(--text-secondary)', textDecoration: 'line-through', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {f.currentValue}
                              </span>
                              <span style={{ color: 'var(--text-muted)' }}>→</span>
                              <strong style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {f.aiValue}
                              </strong>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.75rem', borderTop: '1px solid var(--border-light)', paddingTop: '1rem' }}>
          <button className="btn btn-outline btn-sm" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleMerge}>
            Mesclar Selecionados
          </button>
        </div>

      </div>
    </div>
  );
}
